/**
 * @preserve
 * Licenses of bundled dependencies can be found in dependencies-licenses.txt
 */

import { r as registerInstance, h, H as Host, g as getElement, c as createEvent } from './index-21e7e84e.js';
import { initializeStore } from '@seamless/store';
import { connectionName } from '@dh-io-globalelem/vehicle-data-store-connection';
import { getTrackingDispatchers } from '@owc/connection-aem-tracking';
import { getPageEnvironmentVariables, CTATypes, getVehicleDataStoreState } from '@dh-io-globalelem/vehicle-context-automation';

var CtaSizes;
(function (CtaSizes) {
    CtaSizes["SMALL"] = "s";
    CtaSizes["MEDIUM"] = "m";
    CtaSizes["LARGE"] = "l";
})(CtaSizes || (CtaSizes = {}));
var CtaMobileView;
(function (CtaMobileView) {
    CtaMobileView["ICON"] = "icon";
    CtaMobileView["TEXT"] = "text";
})(CtaMobileView || (CtaMobileView = {}));
var ScenarioKeys;
(function (ScenarioKeys) {
    ScenarioKeys["Default"] = "default";
    ScenarioKeys["Alternative"] = "alternative";
})(ScenarioKeys || (ScenarioKeys = {}));
var CtaDirections;
(function (CtaDirections) {
    CtaDirections["Horizontal"] = "horizontal";
    CtaDirections["Vertical"] = "vertical";
})(CtaDirections || (CtaDirections = {}));
var CtaVariant;
(function (CtaVariant) {
    CtaVariant["Primary"] = "primary";
    CtaVariant["Secondary"] = "secondary";
})(CtaVariant || (CtaVariant = {}));

const setCtaButtonsFromService = async (modelseries, subBrand, scenario = ScenarioKeys.Default, callback) => {
    const { loadCtaConfiguration } = await import('@dh-io-globalelem/vehicle-context-automation');
    const configuration = await loadCtaConfiguration(modelseries, subBrand, undefined, scenario);
    if (configuration) {
        const ctas = [configuration.secondary, configuration.primary].filter(Boolean);
        callback(ctas);
    }
};

const setCtaButtonsFromStore = (callback) => {
    const store = initializeStore();
    store.subscribe(connectionName, (state) => {
        if (state && state.cta) {
            const ctas = [state.cta.secondary, state.cta.primary].filter(Boolean);
            callback(ctas);
        }
    });
};

const setCtaButtons = async (callback, modelseries, subBrand, scenario) => {
    if (modelseries) {
        await setCtaButtonsFromService(modelseries, subBrand, scenario, callback);
    }
    else {
        setCtaButtonsFromStore(callback);
    }
};

var TRACKING_DATA_ATTRIBUTES;
(function (TRACKING_DATA_ATTRIBUTES) {
    TRACKING_DATA_ATTRIBUTES["moduleTrackingName"] = "data-aem-moduletrackingname";
    TRACKING_DATA_ATTRIBUTES["aemModuleName"] = "data-aem-modulename";
})(TRACKING_DATA_ATTRIBUTES || (TRACKING_DATA_ATTRIBUTES = {}));
var TRACKING_APPLICATION_NAMES;
(function (TRACKING_APPLICATION_NAMES) {
    TRACKING_APPLICATION_NAMES["pispa"] = "pi_spa";
    TRACKING_APPLICATION_NAMES["aem"] = "aem";
})(TRACKING_APPLICATION_NAMES || (TRACKING_APPLICATION_NAMES = {}));
var TRACKING_EVENT_CATEGORIES;
(function (TRACKING_EVENT_CATEGORIES) {
    TRACKING_EVENT_CATEGORIES["impression"] = "impression";
    TRACKING_EVENT_CATEGORIES["link"] = "link";
})(TRACKING_EVENT_CATEGORIES || (TRACKING_EVENT_CATEGORIES = {}));

const PI_SALESBAR_NAME = 'salesbar_pi';

const pushTrackingEvent = async (element, ctas, index) => {
    const rootElement = element.closest(`[${TRACKING_DATA_ATTRIBUTES.moduleTrackingName}]`);
    if (rootElement) {
        const { 'globalelem-cta-web-component': globalelemCtaVersion } = await getPageEnvironmentVariables('globalelem-cta-web-component', 'globalelem');
        const eventDetailAction = prepareTrackingActionLabel(rootElement);
        const { eventLabel, eventUrl } = prepareTrackingEventLabel(ctas, index);
        const eventToBePushed = {
            event: 'click',
            eventDetail: {
                category: TRACKING_EVENT_CATEGORIES.link,
                action: eventDetailAction,
                label: eventLabel,
                URL: eventUrl,
            },
            application: {
                id: eventDetailAction === PI_SALESBAR_NAME ? TRACKING_APPLICATION_NAMES.pispa : TRACKING_APPLICATION_NAMES.aem,
                version: globalelemCtaVersion,
            },
        };
        getTrackingDispatchers().then((dispatchers) => {
            dispatchers.pushTrackingEvent(eventToBePushed);
        });
    }
};
const prepareTrackingActionLabel = (element) => {
    const evenActionModuleTrackingName = element.getAttribute(TRACKING_DATA_ATTRIBUTES.moduleTrackingName);
    return evenActionModuleTrackingName;
};
const prepareTrackingEventLabel = (ctas, index) => {
    var _a, _b;
    const eventDetails = {
        eventLabel: '',
        eventUrl: '',
    };
    eventDetails.eventLabel = 'cta.' + (index === 0 ? 'secondary' : 'primary') + '.' + ctas[0].type;
    eventDetails.eventUrl = (_a = ctas[0].url) !== null && _a !== void 0 ? _a : undefined;
    return Object.assign(Object.assign({}, eventDetails), { eventLabel: eventDetails.eventLabel.toLowerCase(), eventUrl: (_b = eventDetails.eventUrl) === null || _b === void 0 ? void 0 : _b.toLowerCase() });
};

const ENQUIRY_FORM_PATH = 'passengercars/content-pool/marketing-pool/contact-forms/forms/owcc/owcc-form/_jcr_content.formcomponentexporter.json';
const FORM_LOADER_URL = 'https://assets.oneweb.mercedes-benz.com/plugin/mbmxp-aemf-form-container/latest/loader.js';
const FORM_SUBMIT_EVENT_NAME = 'aemf-form-submit-success';

const ctaWebComponentCss = ".globalelem-cta-web-component__btn-group{display:inline-flex;align-items:center;flex-direction:row}.globalelem-cta-web-component__btn-group>.globalelem-cta-web-component__cta{flex-basis:auto}.globalelem-cta-web-component__btn-group>.globalelem-cta-web-component__cta:nth-child(2){margin-left:var(--wb-spacing-xxs)}.globalelem-cta-web-component__btn-group--vertical{flex-direction:column;align-items:stretch}.globalelem-cta-web-component__btn-group--vertical>.globalelem-cta-web-component__cta:nth-child(2){margin-left:0;margin-top:var(--wb-spacing-xxs)}.skeleton__container{display:flex}.skeleton__item{width:200px;height:56px}.skeleton__item:first-child{margin-right:20px}";
const GlobalelemCtaWebComponentStyle0 = ctaWebComponentCss;

const CtaWebComponentComponent = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.formUrl = '';
        this.formContextData = '';
        this.uniqueKeyPrefix = 'cta_';
        this.buttonBaseClass = '';
        this.handleButtonClick = (event, cta, index) => {
            if (this.tracking) {
                pushTrackingEvent(event.currentTarget, [cta], index);
            }
            // open enquiry modal only if nothing is maintained in the enquiry url
            if (cta.type === CTATypes.Enquiry && cta.url === '#') {
                this.openModal(event);
            }
        };
        this.openModal = (event) => {
            const modal = document.createElement('globalelem-form');
            event.preventDefault();
            modal.setAttribute('form-url', this.formUrl);
            modal.setAttribute('context-data', this.formContextData);
            modal.addEventListener('modalClosed', () => modal.remove());
            document.body.append(modal);
        };
        this.modelseries = undefined;
        this.subbrand = undefined;
        this.scenario = undefined;
        this.icononly = false;
        this.size = CtaSizes.LARGE;
        this.direction = CtaDirections.Horizontal;
        this.showprimaryonly = false;
        this.enquiryformurl = '';
        this.tracking = true;
        this.modernwb = false;
        this.darkmode = false;
        this.ctas = [];
        this.isLoading = true;
        this.isMobileView = false;
        this.hideLabel = false;
        this.isAnalyticsCalled = false;
    }
    async componentWillLoad() {
        this.setCtaButtons();
        this.buttonBaseClass = this.modernwb ? 'wbx-button' : 'wb-button';
    }
    async setCtaButtons() {
        this.ctas = [];
        this.isLoading = true;
        await setCtaButtons((ctas) => {
            if (ctas.length > 0) {
                this.ctas = ctas;
                this.isLoading = false;
                this.prepareEnquiryModal();
            }
        }, this.modelseries, this.subbrand);
    }
    determineSizeCssClass() {
        switch (this.size) {
            case CtaSizes.SMALL:
                return `${this.buttonBaseClass}--small`;
            case CtaSizes.MEDIUM:
                return `${this.buttonBaseClass}--medium`;
            default:
                return `${this.buttonBaseClass}--large`;
        }
    }
    async prepareEnquiryModal() {
        // prepare enquiry modal only if needed
        if (this.ctas.some((cta) => cta.type === CTATypes.Enquiry)) {
            this.formUrl = this.enquiryformurl || `${this.getBaseUrl()}/${ENQUIRY_FORM_PATH}`;
            this.formContextData = JSON.stringify(await this.getFormContextData());
        }
    }
    async getFormContextData() {
        const data = await getVehicleDataStoreState();
        // get everything except 'cta' and 'price' from the vehicle data store
        return Object.assign(Object.assign({}, data), { cta: undefined, price: undefined });
    }
    getBaseUrl() {
        const link = document.querySelector('link[rel=canonical]');
        if (link) {
            const url = new URL(link.getAttribute('href') || '');
            return url.origin;
        }
        return '';
    }
    getDirectionClass() {
        return this.direction === CtaDirections.Vertical ? 'globalelem-cta-web-component__btn-group--vertical' : '';
    }
    getButtonClass(ctasLength, index) {
        let buttonClass = `globalelem-cta-web-component__cta ${this.buttonBaseClass}`;
        if (ctasLength === 1) {
            buttonClass += ` ${this.buttonBaseClass}--primary`;
        }
        else {
            buttonClass += index === 0 ? ` ${this.buttonBaseClass}--secondary` : ` ${this.buttonBaseClass}--primary`;
        }
        buttonClass += ` ${this.determineSizeCssClass()}`;
        if (this.icononly) {
            buttonClass += ` ${this.buttonBaseClass}--icon-only`;
        }
        // we need to manually add dark theme on legacy wb
        if (this.darkmode && !this.modernwb) {
            buttonClass += ` ${this.buttonBaseClass}--theme-dark`;
        }
        return buttonClass;
    }
    render() {
        return (h(Host, { key: 'fc90d12dae789028f73be317b457aa94e8e5ebe1' }, this.isLoading ? (h("div", { class: "skeleton__container" }, h("wb7-skeleton", { class: "skeleton__item" }), h("wb7-skeleton", { class: "skeleton__item" }))) : (h("div", { class: `globalelem-cta-web-component__btn-group ${this.getDirectionClass()}` }, this.ctas
            .filter((_cta, index) => {
            if (this.showprimaryonly) {
                if (this.ctas.length === 1) {
                    return true;
                }
                return index === 1;
            }
            return true;
        })
            .map((cta, index, ctas) => (h("a", { key: this.uniqueKeyPrefix + cta.type, href: cta.url, onClick: (event) => this.handleButtonClick(event, cta, index), class: this.getButtonClass(ctas.length, index) }, !this.icononly && h("span", { class: "globalelem-cta-web-component__cta-label" }, cta.label), this.icononly && (h("wb7-icon", { className: "globalelem-cta-web-component__cta-icon", slot: "icon", "aria-label": cta.label, name: cta.icon, "icon-flip-rtl": true })))))))));
    }
    get el() { return getElement(this); }
};
CtaWebComponentComponent.style = GlobalelemCtaWebComponentStyle0;

const FormComponent = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.modalClosed = createEvent(this, "modalClosed", 7);
        this.formUrl = '';
        this.contextData = '';
        this.renderForm = false;
    }
    async connectedCallback() {
        return this.loadScript();
    }
    async componentWillLoad() {
        this.renderForm = true;
        window.requestAnimationFrame(this.attachFormSubmitEventListener.bind(this));
    }
    disconnectedCallback() {
        var _a;
        (_a = this.form) === null || _a === void 0 ? void 0 : _a.removeEventListener(FORM_SUBMIT_EVENT_NAME, this.onFormSubmit.bind(this));
        this.renderForm = false;
    }
    attachFormSubmitEventListener() {
        var _a;
        (_a = this.form) === null || _a === void 0 ? void 0 : _a.addEventListener(FORM_SUBMIT_EVENT_NAME, this.onFormSubmit.bind(this));
    }
    onFormSubmit() {
        this.closeModal();
    }
    loadScript() {
        return new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${FORM_LOADER_URL}"]`)) {
                return resolve();
            }
            const script = document.createElement('script');
            script.onload = resolve;
            script.onerror = reject;
            script.src = FORM_LOADER_URL;
            document.body.appendChild(script);
        });
    }
    closeModal() {
        var _a;
        this.renderForm = false;
        (_a = this.modalClosed) === null || _a === void 0 ? void 0 : _a.emit();
    }
    render() {
        return (h(Host, { key: 'ccafb866c610749e5654362f0d256315c2221a36' }, this.renderForm && (h("wb7-modal", { variant: "info", "close-on-backdrop-click": true, "close-on-escape-key": true, "scroll-lock": true }, h("wb7-modal-level", null, h("aemf-form-container", { appid: "pi_spa", formname: "aemf-form", formurl: this.formUrl, contextdata: this.contextData, ref: (el) => (this.form = el) }))))));
    }
};

export { CtaWebComponentComponent as globalelem_cta_web_component, FormComponent as globalelem_form };

//# sourceMappingURL=globalelem-cta-web-component_2.entry.js.map